import { useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import {
  Box,
  Button,
  Card,
  CardContent,
  TextField,
  Typography,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
} from '@mui/material'
import {
  Visibility,
  VisibilityOff,
  Login as LoginIcon,
} from '@mui/icons-material'
import { useAuth, AuthCredentials } from '../common/contexts/AuthContext'

function Login() {
  const navigate = useNavigate()
  const location = useLocation()
  const { login, loading, error } = useAuth()

  const [formData, setFormData] = useState<AuthCredentials>({
    clientId: '',
    clientSecret: '',
  })
  const [showClientSecret, setShowClientSecret] = useState(false)
  const [localError, setLocalError] = useState<string | null>(null)

  const handleChange = (field: keyof AuthCredentials) => (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setFormData({ ...formData, [field]: e.target.value })
    setLocalError(null)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLocalError(null)

    // Validate all fields
    if (!formData.clientId.trim()) {
      setLocalError('Client ID is required')
      return
    }
    if (!formData.clientSecret.trim()) {
      setLocalError('Client Secret is required')
      return
    }

    try {
      await login(formData)
      // Redirect to the original destination or dashboard
      const from = (location.state as any)?.from?.pathname || '/'
      navigate(from, { replace: true })
    } catch {
      // Error is handled by AuthContext
    }
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'grey.100',
        p: 2,
      }}
    >
      <Card sx={{ maxWidth: 450, width: '100%' }}>
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Box
              component="img"
              src="/logo.png"
              alt="Infiniwell"
              sx={{ height: 75, mb: 2 }}
            />
            <Typography variant="h5" component="h1" gutterBottom>
              Vitals Orchestrator
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Sign in with your OAuth client credentials
            </Typography>
          </Box>

          {(error || localError) && (
            <Alert severity="error" sx={{ mb: 1.5 }}>
              {localError || error}
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            <TextField
              fullWidth
              label="Client ID"
              value={formData.clientId}
              onChange={handleChange('clientId')}
              margin="normal"
              autoComplete="off"
              placeholder="OAuth Client ID from OpenEMR"
              helperText="Found in OpenEMR Admin > System > API Clients"
            />
            
            <TextField
              fullWidth
              label="Client Secret"
              type={showClientSecret ? 'text' : 'password'}
              value={formData.clientSecret}
              onChange={handleChange('clientSecret')}
              margin="normal"
              autoComplete="off"
              placeholder="OAuth Client Secret from OpenEMR"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowClientSecret(!showClientSecret)}
                      edge="end"
                    >
                      {showClientSecret ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} /> : <LoginIcon />}
              sx={{ mt: 1.5, mb: 1 }}
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </Button>
          </form>

          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mt: 2 }}>
            This uses OAuth 2.0 Client Credentials to authenticate.
            <br />
            Ensure your API client is registered and enabled in OpenEMR.
          </Typography>
        </CardContent>
      </Card>
    </Box>
  )
}

export default Login
